import { yupResolver } from '@hookform/resolvers/yup';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import 'react-day-picker/lib/style.css';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector, useStore } from 'react-redux';
import * as Yup from 'yup';
import { AMENITY_PARKING } from '../../../helper/types';
import { clearAmenity, getAmenityV2, getBuildingAmenitiesV2 } from '../../../redux/actions/amenities';
import {
  addBookingV3,
  clearAmenityAvailabilityV2,
  clearBookingCostBreakdown,
  clearBookingError,
  getBookingsCalendarV3,
  getBookingsListV3
} from '../../../redux/actions/bookingParAm';
import { getVopayUser, resetPaymentUserData } from '../../../redux/actions/payments';
import {
  GET_AMENITY_AVAILABILITY_V2,
  GET_BOOKING_COST_BREAKDOWN,
  GET_VOPAY_USER,
  GET_VOPAY_USER_PAYMENT_METHODS
} from '../../../redux/constants';
import { requestFail, requestPending, requestSuccess } from '../../../utils/status';
import { LoadingSpinner } from '../../payments/PaymentComponents';
import { resolveDefaultCreditCardState } from './bookingConfirmationhelper';
import {
  amenityFormRequiredButMissing,
  amenityRequiresReservationForm,
  BOOKING_FORM_NO_TIME,
  buildBookingCreatePayload,
  buildBookingValidationSchema
} from './bookingFormHelpers';
import { useBookingDetailForm } from './useBookingDetailForm';
import { changeOpenedModal } from '../../../redux/actions/headerModal';
import { setToast } from '../../../redux/actions/window';
import { delay } from 'lodash';
import { useTranslation } from 'react-i18next';
import { WizardModalLayout } from '../../../components/ModalScrollProgress';
import {
  getBookingWizardModalProps,
  StyledModalCardV2
} from '../../../components/modalV2';
import AddBookingConfirmationStepV2 from './AddBookingConfirmationStepV2';
import AddBookingDetailStepV2 from './AddBookingDetailStepV2';
import AddBookingFormConsentStepV2 from './AddBookingFormConsentStepV2';
import { buildListV3Params } from './bookingsFilterHelpers';
import { useBookingAvailabilityFetcher } from '../../properties/Unit&Amenities/Amenities/V2/useBookingAvailabilityFetcher';
import {
  getAddBookingWizardChromeProps,
  getBookingConfirmationStepIndex,
  getBookingFormStepIndex,
  getBookingWizardStepCount
} from './bookingWizardConfigV2';
import WizardChromeV2 from '../../../components/WizardV2/WizardChromeV2';
import {
  BookingWizardStepPane,
  WizardFooterButtonPrimary,
  WizardFooterButtonSecondary
} from '../../../components/WizardV2/wizardStylesV2';

const AddBookingV2 = () => {
  const { t } = useTranslation();

  const [selectedAmenityId, setSelectedAmenityId] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);
  const prefillAppliedRef = useRef(false);
  const lastFetchedAmenityIdRef = useRef(null);

  const dispatch = useDispatch();
  const store = useStore();

  const { error, availabilitySlotsV2, status: bookingStatus, bookingCostBreakdown } = useSelector(
    (state) => state.parAmBooking
  );
  const availabilitySlots = availabilitySlotsV2;
  const slotsLoading = bookingStatus === requestPending(GET_AMENITY_AVAILABILITY_V2);
  const { modal, data: modalData } = useSelector((state) => state.headerModal);
  const { amenities, amenity } = useSelector((state) => state.amenities);
  const { userVopayAccountData, userVopayAccounStatus, userPaymentMethods, userPaymentMethodsStatus } =
    useSelector((state) => state.addPayment);

  const requiresForm = amenityRequiresReservationForm(amenity);
  const requiresPayment = !!amenity?.require_payment;
  const formStepIndex = getBookingFormStepIndex(requiresForm);
  const confirmationStepIndex = getBookingConfirmationStepIndex(requiresForm);
  const totalSteps = getBookingWizardStepCount(requiresForm);

  const [verifyTimeslot, setVerifyTimeslot] = useState(false);

  const validationSchema = useMemo(
    () =>
      buildBookingValidationSchema({
        yup: Yup,
        t,
        amenity,
        availabilitySlots,
        verifyTimeslot,
        selectedAmenityId,
        requiresReservationForm: requiresForm
      }),
    [t, verifyTimeslot, availabilitySlots, amenity, selectedAmenityId, requiresForm]
  );

  const schemaRef = useRef(validationSchema);
  schemaRef.current = validationSchema;

  const dynamicYupResolver = useCallback((values, context, options) => {
    return yupResolver(schemaRef.current)(values, context, options);
  }, []);

  const {
    control,
    handleSubmit,
    watch,
    trigger,
    getValues,
    setValue,
    formState: { errors },
    resetField,
    clearErrors,
    reset
  } = useForm({
    resolver: dynamicYupResolver,
    mode: 'onChange',
    defaultValues: {
      startTime: BOOKING_FORM_NO_TIME,
      endTime: BOOKING_FORM_NO_TIME,
      endDate: undefined,
      reservationFormConsent: false
    }
  });

  const watchAllFields = watch();
  const watchAmenityId = watch('amenity_id');
  const watchStartDate = watch('startDate');
  const watchEndDate = watch('endDate');
  const watchBuilding = watch('building');
  const watchStartTime = watch('startTime');
  const watchEndTime = watch('endTime');
  const watchUser = watch('user');

  const vopayCheckLoading =
    requiresPayment &&
    !!watchUser?.value &&
    userVopayAccounStatus === requestPending(GET_VOPAY_USER);
  const vopayCheckDone = userVopayAccounStatus === requestSuccess(GET_VOPAY_USER);
  const residentMissingCC =
    requiresPayment &&
    !!watchUser?.value &&
    vopayCheckDone &&
    userVopayAccountData?.hasDefaultCC !== true;
  const paymentFieldsDisabled = vopayCheckLoading || residentMissingCC;

  useBookingAvailabilityFetcher({
    watchStartDate,
    watchAmenityId,
    type: null,
    dispatch,
    active: true,
    setVerifyTimeslot
  });

  const {
    allowMultiDayBooking,
    endTimeOptions,
    disabledDates,
    disabledEndDate,
    minBookingMinutes
  } = useBookingDetailForm({
    amenity,
    availabilitySlots,
    watchStartTime,
    watchEndDate,
    watchStartDate,
    watchEndTime,
    watchAmenityId,
    watchBuilding,
    setValue,
    clearErrors,
    isFormInitialized: true
  });

  useEffect(() => {
    if (watchStartDate) {
      trigger('startDate');
    }
  }, [watchStartDate, amenity, trigger]);

  useEffect(() => {
    if (
      bookingStatus === requestSuccess(GET_AMENITY_AVAILABILITY_V2) ||
      bookingStatus === requestFail(GET_AMENITY_AVAILABILITY_V2)
    ) {
      setVerifyTimeslot(true);
    }
  }, [bookingStatus]);

  useEffect(() => {
    setValue('reservationFormConsent', false);
    clearErrors('reservationFormConsent');
  }, [watchAmenityId, setValue, clearErrors]);

  useEffect(() => {
    if (!requiresPayment) {
      dispatch(resetPaymentUserData());
      return;
    }
    if (!watchUser?.value || !watchBuilding?.value) {
      if (!watchUser?.value) {
        dispatch(resetPaymentUserData());
      }
      return;
    }
    dispatch(
      getVopayUser({
        buildingID: watchBuilding.value,
        userId: watchUser.value,
        fail: () =>
          dispatch(
            setToast({
              toastShow: true,
              toastMessage: t('Failed to get user!'),
              modal: 'errorToast'
            })
          )
      })
    );
  }, [requiresPayment, watchUser?.value, watchBuilding?.value, dispatch, t]);

  useEffect(() => {
    if (wizardStep >= totalSteps) {
      setWizardStep(Math.max(0, totalSteps - 1));
    }
  }, [totalSteps, wizardStep]);

  const showBookingModal = modal === 'addBookingV2';

  useEffect(() => {
    if (showBookingModal) {
      setWizardStep(0);
      dispatch(resetPaymentUserData());
    }
  }, [modal, showBookingModal, dispatch]);

  useEffect(() => {
    if (!showBookingModal) {
      prefillAppliedRef.current = false;
      return;
    }

    const preselectedAmenity = modalData?.preselectedAmenity;
    const preselectedBuilding = modalData?.preselectedBuilding;

    if (!preselectedAmenity && !preselectedBuilding) {
      return;
    }
    if (prefillAppliedRef.current) {
      return;
    }
    prefillAppliedRef.current = true;

    if (preselectedBuilding?.id) {
      setValue('building', {
        value: preselectedBuilding.id,
        label: preselectedBuilding.label
      });
      dispatch(getBuildingAmenitiesV2({ id: preselectedBuilding.id }));
    }

    if (preselectedAmenity?.id) {
      setValue('amenity_id', preselectedAmenity.id);
      setSelectedAmenityId(preselectedAmenity.type_id || 0);
    }
  }, [showBookingModal, modalData, setValue, dispatch]);

  const onClose = () => {
    reset({
      startTime: BOOKING_FORM_NO_TIME,
      endTime: BOOKING_FORM_NO_TIME,
      endDate: undefined,
      reservationFormConsent: false
    });
    setSelectedAmenityId(0);
    prefillAppliedRef.current = false;
    lastFetchedAmenityIdRef.current = null;
    dispatch(clearAmenity());
    dispatch(clearAmenityAvailabilityV2());
    dispatch(clearBookingCostBreakdown());
    dispatch(
      changeOpenedModal({
        modal: '',
        data: null
      })
    );
    dispatch(clearBookingError());
    dispatch(resetPaymentUserData());
    setWizardStep(0);
  };

  const customSubmit = (values) => {
    setIsSubmitting(true);

    const amenityRow = amenities.find((a) => a.id === parseInt(values.amenity_id, 10));

    dispatch(
      addBookingV3({
        body: buildBookingCreatePayload(values, amenityRow, amenity),
        success: () => {
          const latest = store.getState();
          const calendarSnap = latest.parAmBooking.lastBookingsCalendarV3Params;
          const buildingId = latest.building.globalBuildingId;
          const orgId = latest.Organization.globalOrgId;
          onClose();
          if (
            calendarSnap &&
            Number(calendarSnap.building) === Number(buildingId)
          ) {
            dispatch(getBookingsCalendarV3(calendarSnap));
          }
          dispatch(
            getBookingsListV3(
              buildListV3Params({
                buildingId,
                page: 1,
                search: latest.parAmBooking.search,
                orgId
              })
            )
          );
          dispatch(
            setToast({
              toastShow: true,
              toastMessage: t('Booking has been successfully created!')
            })
          );
          setIsSubmitting(false);
        },

        fail: () => {
          dispatch(
            setToast({
              toastShow: true,
              toastMessage: t('Failed to create booking!'),
              modal: 'errorToast'
            })
          );
          setIsSubmitting(false);
        }
      })
    );
  };

  useEffect(() => {
    const amenityIdInt = parseInt(watchAmenityId, 10);

    if (!Number.isFinite(amenityIdInt) || amenityIdInt <= 0) {
      lastFetchedAmenityIdRef.current = null;
      return;
    }

    if (lastFetchedAmenityIdRef.current === amenityIdInt) {
      return;
    }
    lastFetchedAmenityIdRef.current = amenityIdInt;

    const selectedTypeId = amenities.find(
      (a) => parseInt(a.id, 10) === amenityIdInt
    )?.type_id;
    setSelectedAmenityId(selectedTypeId || 0);

    dispatch(
      getAmenityV2({
        id: amenityIdInt,
        fail: () => {
          dispatch(
            setToast({
              toastShow: true,
              toastMessage: t('Failed to get amenity!'),
              modal: 'errorToast'
            })
          );
        }
      })
    );
  }, [watchAmenityId, amenities, dispatch, t]);

  const getFilteredAmenities = useMemo(() => {
    return amenities.filter((map) => map.allow_booking);
  }, [amenities]);

  useEffect(() => {
    const building = watchBuilding;
    if (building?.value) {
      dispatch(getBuildingAmenitiesV2({ id: building?.value }));
    }
  }, [watchBuilding, dispatch]);

  useEffect(() => {
    const availableTimeTest = async () => {
      await trigger(['startDate']);
    };
    if (watchStartDate && verifyTimeslot) {
      delay(function () {
        availableTimeTest();
      }, 500);
    }
  }, [availabilitySlots, verifyTimeslot, trigger, watchStartDate]);

  const getDetailValidationFields = () => {
    const fields = ['building', 'amenity_id', 'user', 'startDate'];
    if (allowMultiDayBooking) {
      fields.push('endDate');
    }
    fields.push('startTime', 'endTime');
    if (selectedAmenityId === AMENITY_PARKING) {
      fields.push('make', 'model', 'color', 'plate_number');
    }
    return fields;
  };

  const handleNextFromDetails = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (paymentFieldsDisabled) {
      return;
    }

    if (amenityFormRequiredButMissing(amenity)) {
      dispatch(
        setToast({
          toastShow: true,
          toastMessage: t('book_amenity_form_missing_error', {
            defaultValue:
              'This amenity requires a form but none is configured. Please contact your administrator.'
          }),
          modal: 'errorToast'
        })
      );
      return;
    }

    const ok = await trigger(getDetailValidationFields(), { shouldFocus: true });
    if (!ok) return;

    setWizardStep(requiresForm ? formStepIndex : confirmationStepIndex);
  };

  const handleNextFromForm = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    const ok = await trigger(['reservationFormConsent'], { shouldFocus: true });
    if (ok) {
      setWizardStep(confirmationStepIndex);
    }
  };

  const handleBack = () => {
    if (wizardStep === confirmationStepIndex) {
      setWizardStep(requiresForm ? formStepIndex : 0);
      return;
    }
    if (wizardStep === formStepIndex) {
      setWizardStep(0);
    }
  };

  const onFormSubmit = (e) => {
    if (wizardStep !== confirmationStepIndex) {
      e.preventDefault();
      return;
    }
    handleSubmit(customSubmit)(e);
  };

  const safeStep = Math.min(Math.max(0, wizardStep), totalSteps - 1);
  const modalTitle = t('Book amenity');
  const wizardChromeProps = getAddBookingWizardChromeProps(safeStep, t, {
    modalTitle,
    requiresForm
  });

  const isDetailsStep = safeStep === 0;
  const isFormStep = requiresForm && safeStep === formStepIndex;
  const isConfirmationStep = safeStep === confirmationStepIndex;
  const costBreakdownLoading =
    requiresPayment && bookingStatus === requestPending(GET_BOOKING_COST_BREAKDOWN);
  const paymentMethodLoading =
    requiresPayment &&
    isConfirmationStep &&
    userPaymentMethodsStatus === requestPending(GET_VOPAY_USER_PAYMENT_METHODS);
  const paymentMethodsLoaded =
    userPaymentMethodsStatus === requestSuccess(GET_VOPAY_USER_PAYMENT_METHODS) ||
    userPaymentMethodsStatus === requestFail(GET_VOPAY_USER_PAYMENT_METHODS);
  const defaultCreditCardState = useMemo(
    () =>
      requiresPayment && isConfirmationStep
        ? resolveDefaultCreditCardState(userPaymentMethods, {
            loading: paymentMethodLoading,
            loaded: paymentMethodsLoaded
          })
        : { loading: false, isUsable: true, showExpiredError: false },
    [
      requiresPayment,
      isConfirmationStep,
      userPaymentMethods,
      paymentMethodLoading,
      paymentMethodsLoaded
    ]
  );
  const submitDisabled =
    isSubmitting ||
    costBreakdownLoading ||
    paymentMethodLoading ||
    (requiresPayment && isConfirmationStep && !bookingCostBreakdown) ||
    (requiresPayment && isConfirmationStep && !defaultCreditCardState.isUsable);

  const footerActions = isDetailsStep ? (
    <>
      <WizardFooterButtonSecondary type="button" onClick={onClose}>
        {t('Cancel')}
      </WizardFooterButtonSecondary>
      <WizardFooterButtonPrimary
        type="button"
        onClick={handleNextFromDetails}
        disabled={paymentFieldsDisabled}
        style={{ position: 'relative' }}>
        {t('Next', { defaultValue: 'Next' })}
        {vopayCheckLoading ? (
          <LoadingSpinner size={18} style={{ position: 'absolute', right: '1.25rem' }} />
        ) : null}
      </WizardFooterButtonPrimary>
    </>
  ) : isFormStep ? (
    <>
      <WizardFooterButtonSecondary type="button" onClick={handleBack}>
        {t('Back')}
      </WizardFooterButtonSecondary>
      <WizardFooterButtonPrimary type="button" onClick={handleNextFromForm}>
        {t('Next', { defaultValue: 'Next' })}
      </WizardFooterButtonPrimary>
    </>
  ) : (
    <>
      <WizardFooterButtonSecondary type="button" onClick={handleBack}>
        {t('Back')}
      </WizardFooterButtonSecondary>
      <WizardFooterButtonPrimary type="submit" disabled={submitDisabled}>
        {t('Book amenity')}
      </WizardFooterButtonPrimary>
    </>
  );

  return (
    <form name="AddBookingForm" onSubmit={onFormSubmit}>
      <WizardModalLayout
        Modal={StyledModalCardV2}
        modalProps={getBookingWizardModalProps(isConfirmationStep)}
        show={showBookingModal}
        onClose={onClose}
        width="460px"
        bodyGap="12px"
        deps={[wizardStep, showBookingModal, requiresForm]}
        error={error}
        footerActions={footerActions}>
        <WizardChromeV2 {...wizardChromeProps}>
          {isDetailsStep && (
            <BookingWizardStepPane>
              <AddBookingDetailStepV2
                t={t}
                control={control}
                errors={errors}
                watch={watch}
                resetField={resetField}
                setValue={setValue}
                clearErrors={clearErrors}
                amenity={amenity}
                disabledDates={disabledDates}
                disabledEndDate={disabledEndDate}
                getFilteredAmenities={() => getFilteredAmenities}
                selectedAmenityId={selectedAmenityId}
                availabilitySlots={availabilitySlots}
                endTimeOptions={endTimeOptions}
                watchStartTime={watchStartTime}
                watchStartDate={watchStartDate}
                watchAllFields={watchAllFields}
                slotsLoading={slotsLoading}
                allowMultiDayBooking={allowMultiDayBooking}
                paymentFieldsDisabled={paymentFieldsDisabled}
                showResidentPaymentError={residentMissingCC}
              />
            </BookingWizardStepPane>
          )}

          {isFormStep && (
            <AddBookingFormConsentStepV2
              t={t}
              control={control}
              errors={errors}
              amenity={amenity}
            />
          )}

          {isConfirmationStep && (
            <AddBookingConfirmationStepV2
              t={t}
              getValues={getValues}
              amenity={amenity}
            />
          )}
        </WizardChromeV2>
      </WizardModalLayout>
    </form>
  );
};

export default AddBookingV2;
